import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import defaultRegels from '../data/regels.json';
import { genereerMaandrooster as genereerMaandroosterEngine } from '../engine/rooster-engine.js';
import { genereerMaandViaBlokken } from '../engine/blok-generator.js';
import { analyseerWeek } from '../engine/analyseer-week.js';
import { bouwAfwezigheidMap, bouwAfwezigheidTypeMap } from '../data/afwezigheid.js';
import { bouwAfwezigheidUrenMap } from '../data/verlofuren.js';
import { SCHOOLVAKANTIES_ZUID, vakantieDagenInWeek } from '../data/vakanties.js';
import { isoWeekNummer, maandWeken, weekNaarMaandag } from '../data/maand.js';
import { logError } from '../lib/logger.js';
import { leesActieveOmgeving, STANDAARD_OMGEVING } from '../lib/omgeving.js';
import { KANTOOR_UREN_STANDAARD } from '../lib/roostercodes.js';
import { vandaagNL } from '../lib/tijd.js';
// Geen import-lus: wijzigingSync → api.js → { huidigeGebruiker, omgeving } en die twee
// importeren zelf niets. De store houdt zo geen netwerkcode, alleen deze melding.
import { meldWijziging } from '../lib/wijzigingSync.js';

// Verse init: ISO-8601 week + bijbehorend ISO-jaar (rond de jaarwisseling kan het
// ISO-jaar afwijken van het kalenderjaar; weekNaarMaandag verwacht het ISO-jaar).
const initIso = isoWeekNummer(new Date());

// Positie i vanaf (startWeek, startIsoJaar) → het ECHTE ISO-weeknummer + ISO-jaar.
// Blind doortellen (startWeek + i) loopt aan het eind van een ISO-jaar door naar week 54
// en 55; die bestaan niet, en hun sleutel (`2026-54`) matcht nooit op een week die als
// `2027-1` is vastgelegd — zo'n vastgelegde januariweek zou stil genegeerd en opnieuw
// gegenereerd worden. Daarom via de kalender rekenen (de maandag + i weken) en het
// ISO-nummer daarvan aflezen; geen tweede ISO-wiskunde, dezelfde helpers als de rest.
function weekOpPositie(startWeek, startIsoJaar, i) {
  const maandag = weekNaarMaandag(startWeek, startIsoJaar);
  maandag.setDate(maandag.getDate() + i * 7);
  return isoWeekNummer(maandag); // { week, isoJaar }
}

function maakMedewerkerSleutel(naam) {
  return naam.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

// Geëxporteerd zodat serverSync.laadVanServer dezelfde reset kan toepassen als
// importBackup (verderop in dit bestand) — één definitie, geen tweede lijst velden die
// stil uit de pas kan lopen.
export const resetRooster = {
  maandrooster: null, roosterStatus: 'idle', roosterLog: [], conflicten: [], caoOvertredingen: [],
};

// ── Banner-conflicten: ALTIJD vers uit het rooster dat er NU staat ───────────
// Een opgeslagen week draagt de conflicten van de generatie van TOEN, in het formaat
// van toen. Een vastgelegde week komt ongewijzigd terug uit de vast-tak in draaiMaandrooster,
// en een bewaard maandrooster is een diepe kopie van diezelfde weken — die conflicten
// kunnen dus niet meer kloppen met de huidige dienst-instellingen (min_personen aangepast
// na het vastleggen), en na een veldnaam-wijziging leest de banner er lege waarden uit.
// Daarom leidt de banner zijn lijst af uit week.rooster in plaats van uit week.conflicten.
//
// Zelfde aanroepvorm als wijzigCel en als de server (rooster-service.js:98-102): afwezigheid,
// weekCaps, vakantieDagen. Puur lezend — week.conflicten en week.analyse blijven ongemoeid,
// en het opgeslagen weekobject wordt niet gemuteerd.
function verseConflicten(week, { regels, afwezigheid, vakanties }) {
  if (!week?.rooster) return [];
  const isoJaar = week.isoJaar;
  const analyse = analyseerWeek(
    week.rooster, regels, week.weeknummer, isoJaar, week.schoolweekType,
    {
      afwezigheid: bouwAfwezigheidMap(afwezigheid, week.weeknummer, isoJaar),
      weekCaps: week.weekCaps,
      vakantieDagen: new Set(vakantieDagenInWeek(week.weeknummer, isoJaar, vakanties)),
    },
  );
  return analyse.conflicten;
}

// ── Gemeente-hulpfuncties (immuutbaar) ──────────────────────────────────────

function updateGemeenteIn(regels, gemKey, updates) {
  return {
    ...regels,
    gemeenten: { ...regels.gemeenten, [gemKey]: { ...regels.gemeenten[gemKey], ...updates } },
  };
}

function updateDienstIn(regels, gemKey, dienstKey, updates) {
  const gem = regels.gemeenten[gemKey];
  return updateGemeenteIn(regels, gemKey, {
    diensten: { ...gem.diensten, [dienstKey]: { ...gem.diensten[dienstKey], ...updates } },
  });
}

function removeDienstIn(regels, gemKey, dienstKey) {
  const gem = regels.gemeenten[gemKey];
  const { [dienstKey]: _, ...rest } = gem.diensten;
  return updateGemeenteIn(regels, gemKey, { diensten: rest });
}

// ── Per-omgeving localStorage-bucket (stap 6a) ───────────────────────────────
// Elke omgeving krijgt een EIGEN bucket ('sass-rooster-v4::arnhem_eo',
// 'sass-rooster-v4::maastricht'), zodat de lades zich nooit vermengen.
// STANDAARD_OMGEVING komt uit lib/omgeving.js — dezelfde terugval die de back-up-naam
// en de import-check gebruiken, zodat die drie nooit naar een andere lade wijzen.

// De sleutel wordt bij ELKE lees-/schrijfactie opnieuw samengesteld — niet één keer bij het
// aanmaken van de store — zodat de bucket de actieve omgeving vanzelf volgt zodra die wisselt,
// zonder de store opnieuw te hoeven maken. Geen keuze opgeslagen -> de standaard-omgeving.
function bucketSleutel(naam) {
  return `${naam}::${leesActieveOmgeving() || STANDAARD_OMGEVING}`;
}

// Alle drie de operaties zijn afgeschermd: is localStorage onbereikbaar (private mode), dan
// draait de app door zonder persistentie in plaats van te crashen — zelfde keuze als lib/omgeving.js.
const omgevingStorage = {
  getItem: (naam) => {
    try {
      const waarde = localStorage.getItem(bucketSleutel(naam));
      if (waarde !== null) return waarde;
      // Terugval: data van vóór de omgevingen-laag staat nog onder de KALE sleutel en hoort
      // per definitie bij de standaard-omgeving (er was maar één lade). Alleen LEZEN, niet
      // verplaatsen: de oude sleutel blijft staan, dus een terugrol vindt 'm nog. Zodra de
      // store één keer wegschrijft bestaat de bucket en is deze terugval uitgewerkt.
      if (bucketSleutel(naam) !== `${naam}::${STANDAARD_OMGEVING}`) return null;
      return localStorage.getItem(naam);
    } catch {
      return null; // localStorage onbereikbaar -> geen opgeslagen state
    }
  },
  setItem: (naam, waarde) => {
    try {
      localStorage.setItem(bucketSleutel(naam), waarde);
    } catch {
      /* localStorage onbereikbaar -> de state leeft alleen in het geheugen */
    }
  },
  removeItem: (naam) => {
    try {
      localStorage.removeItem(bucketSleutel(naam));
    } catch {
      /* idem */
    }
  },
};

// ── Store ────────────────────────────────────────────────────────────────────

export const useRoosterStore = create(
  persist(
    (set, get) => ({
      regels: defaultRegels,
      weeknummer: initIso.week,
      jaar: initIso.isoJaar,
      maandSelectie: null, // { jaar, maand } als een hele maand gekozen is; null = 4-weken-modus
      // Welke maand je in Teamrooster/Mijn Rooster bekijkt — puur een kijk-venster, LOS van
      // maandSelectie (dat aan het genereren hangt). Niet in partialize: verse load = huidige maand.
      bekekenMaand: null, // { jaar, maand }
      zetBekekenMaand: (jaar, maand) => set({ bekekenMaand: { jaar, maand } }),

      // Handmatige week-navigatie verlaat de maand-modus
      setWeeknummer: (wn) => set({ weeknummer: wn, maandSelectie: null, ...resetRooster }),

      ...resetRooster,

      afwezigheid: [],
      // Schoolvakanties: geseed in localStorage (bron van waarheid). De engine leest ze
      // nog NIET — dat is stap 3; nu puur persisteren zodat de lijst leeft + bewerkbaar wordt.
      vakanties: SCHOOLVAKANTIES_ZUID,
      bijzonderheden: {}, // { [weeknummer]: { ma: '', di: '', ... } }
      // Handmatige S/K-cel-markering (stukje 1/3): { [weeknummer]: { [naam]: { [dag]: 'S' | 'K' | 'leeg' } } }.
      // Display-only — wordt pas in stukje 2/3 door getCel gelezen. Geen bezetting, geen engine.
      celOverrides: {},
      // Afgevinkte meldingen uit het paneel boven de matrix: { [meldingsleutel]: { peil, op } }.
      // TEAMBREED en niet per persoon — wat de één heeft nagekeken hoeft de ander niet over te
      // doen. Client-eigen data, dus dezelfde weg als celOverrides: partialize → BACKUP_VELDEN
      // → de body-categorie in PUT /api/data (server/src/index.js:289-295).
      //
      // De sleutel komt uit lib/meldingsleutel.js en draagt ALTIJD isoJaar. Bewust anders dan
      // celOverrides hierboven, dat op weeknummer ZONDER isoJaar is gesleuteld: daar kan week 1
      // van twee jaren op dezelfde sleutel landen. Die fout is hier niet overgenomen.
      //
      // `peil` is de situatie op het moment van afvinken (bezet bij onderbezetting, lengte bij
      // een reeks); verandert die, dan vervalt het vinkje — de vergelijking staat op één plek,
      // in vinkStand (lib/meldingsleutel.js).
      afgevinkteMeldingen: {},
      ruilverzoeken: [], // dienstruil-verzoeken (opslag klaarzetten; UI + vorm volgen)
      // Goedgekeurde ruilen — SERVER-EIGEN data, hier alleen om te LEZEN. Komt binnen via
      // laadVanServer (serverSync.js:84 spreidt de volledige GET-respons in de store) en
      // wordt nooit teruggeschreven: hij staat niet in BACKUP_VELDEN (lib/backup.js:20-31),
      // dus de PUT draagt hem niet, en de server beschermt hem daar bovenop nog eens
      // (server/src/index.js:300 neemt hem uit de OUDE lade).
      // BEWUST NIET in partialize: in localStorage zou hij een herlaad overleven en dan als
      // verouderde kopie terugkomen — dezelfde bevroren-kopie-fout als samenvattingPerBlok.
      // Vers van de server of niet aanwezig; deze [] is er alleen zodat lezers vóór de eerste
      // load niet op undefined stuiten.
      ruilWissels: [],
      // Vastgelegde (vergrendelde) weken — bewaard in localStorage en als vaste invoer
      // aan de engine doorgegeven. Sleutel: `${isoJaar}-${weeknummer}` (STREEPJE).
      // Waarde: { isoJaar, weeknummer, blok, vastgelegdOp, week } — week = volledig
      // engine-weekobject (rooster, urenTeller, conflicten, log, ...).
      vastgelegdeWeken: {},
      // Bewaarde maandroosters — de laatste generatie per maand, bewaard in localStorage
      // zodat een maand bij maandwissel/reload niet opnieuw gegenereerd hoeft te worden.
      // Sleutel: `${jaar}-${maand}`. Waarde: diepe kopie van { weken, samenvattingPerBlok }.
      maandroosters: {},

      setBijzonderheid: (weeknummer, dag, tekst) =>
        set(state => ({
          bijzonderheden: {
            ...state.bijzonderheden,
            [weeknummer]: { ...(state.bijzonderheden[weeknummer] || {}), [dag]: tekst },
          },
        })),

      // Handmatige S/K-markering per (week, medewerker-naam, dag). Display-only: GEEN
      // analyseerWeek/engine-aanroep (informatieve codes, geen bezetting). De STRING 'leeg'
      // is een expliciete markering die we zetten; null/'' verwijdert (terug naar automatisch).
      //
      // `uren` is optioneel en telt ALLEEN bij 'K': een kantoormarkering mag een eigen
      // urenaantal dragen en wordt dan als { code: 'K', uren } opgeslagen. 'S' en 'leeg'
      // blijven de kale string die ze altijd waren, en bestaande markeringen worden niet
      // aangeraakt — die vallen bij het LEZEN terug op 8 (roostercodes.js, overrideUren).
      // Een ontbrekend of onbruikbaar getal wordt de standaard in plaats van in de opslag
      // te belanden; het verwijdergedrag hieronder is letterlijk ongewijzigd.
      setCelOverride: ({ weeknummer, naam, dag, waarde, uren }) =>
        set(state => {
          const co = state.celOverrides;
          if (!waarde) {
            const week = co[weeknummer];
            if (!week?.[naam] || !(dag in week[naam])) return {}; // niets te verwijderen
            const nieuweNaam = { ...week[naam] };
            delete nieuweNaam[dag];
            const nieuweWeek = { ...week };
            if (Object.keys(nieuweNaam).length === 0) delete nieuweWeek[naam];
            else nieuweWeek[naam] = nieuweNaam;
            const nieuw = { ...co };
            if (Object.keys(nieuweWeek).length === 0) delete nieuw[weeknummer];
            else nieuw[weeknummer] = nieuweWeek;
            return { celOverrides: nieuw };
          }
          const week = co[weeknummer] || {};
          const nieuweWaarde = waarde === 'K'
            ? { code: 'K', uren: Number.isFinite(uren) ? uren : KANTOOR_UREN_STANDAARD }
            : waarde;
          return {
            celOverrides: {
              ...co,
              [weeknummer]: { ...week, [naam]: { ...(week[naam] || {}), [dag]: nieuweWaarde } },
            },
          };
        }),

      // Een melding af- of aanvinken. `peil` is de peilwaarde van NU (meldingPeil); geef
      // peil === null mee om het vinkje weg te halen — zelfde vorm als setCelOverride
      // hierboven, waar een lege waarde de sleutel verwijdert in plaats van hem op iets leegs
      // te zetten. Zo blijft het veld schoon: alleen wat écht afgevinkt is staat erin.
      // Bewust GEEN eigen vergelijking hier: of een vinkje nog geldt bepaalt vinkStand
      // (lib/meldingsleutel.js), deze setter legt alleen vast.
      zetMeldingAfgevinkt: ({ sleutel, peil }) =>
        set(state => {
          if (!sleutel) return {}; // geen sleutel = niet afvinkbaar; niets te doen
          const am = state.afgevinkteMeldingen || {};
          if (peil === null || peil === undefined) {
            if (!(sleutel in am)) return {}; // niets te verwijderen
            const nieuw = { ...am };
            delete nieuw[sleutel];
            return { afgevinkteMeldingen: nieuw };
          }
          // `op` is een INSTANT, geen kalenderdag — toISOString is daar het juiste antwoord
          // (lib/tijd.js:10-11). Puur informatief; de vergelijking gaat over `peil`.
          return {
            afgevinkteMeldingen: { ...am, [sleutel]: { peil, op: new Date().toISOString() } },
          };
        }),

      // Verlof/ziekte werkt live door in de matrix (getCel via afwTypeWeek) en de signalen
      // (weekAnalyses); daarom bewust GEEN ...resetRooster meer — de matrix blijft staan i.p.v.
      // te verdwijnen tot een nieuwe generatie. Dat de VERDELING van de diensten daarmee nog
      // van vóór de wijziging is, wordt niet apart gemeld: het verlof staat al als V/Z in de
      // matrix, en een dienst die erdoor onbemand raakt komt specifieker terug als
      // onderbezetting in Knelpunten.
      addAfwezigheid: (item) =>
        set(state => ({
          afwezigheid: [...state.afwezigheid, { ...item, id: Date.now().toString() }],
        })),

      removeAfwezigheid: (id) =>
        set(state => ({
          afwezigheid: state.afwezigheid.filter(a => a.id !== id),
        })),

      // ── Schoolvakanties ──────────────────────────────────────────────────
      // Spiegelt add/removeAfwezigheid + een wijzig-setter. ...resetRooster zodat een
      // bewerking het zichtbare rooster invalideert (forward-correct voor stap 3, wanneer
      // de engine de vakanties wél leest). Identificatie via stabiele seed-id.
      addVakantie: (item) =>
        set(state => ({
          vakanties: [...state.vakanties, { ...item, id: Date.now().toString() }],
          ...resetRooster,
        })),

      wijzigVakantie: (id, updates) =>
        set(state => ({
          vakanties: state.vakanties.map(v => (v.id === id ? { ...v, ...updates } : v)),
          ...resetRooster,
        })),

      removeVakantie: (id) =>
        set(state => ({
          vakanties: state.vakanties.filter(v => v.id !== id),
          ...resetRooster,
        })),

      // ── Vastgelegde weken ────────────────────────────────────────────────
      // Vastleggen/ontgrendelen mag het zichtbare rooster NIET wissen: bewust
      // GEEN ...resetRooster hier (anders verdwijnt de net gegenereerde weergave).

      // Melden aan het wijzigingslogboek gebeurt telkens NA set(), met een in de closure
      // opgevangen record: zo blijft de reducer zelf vrij van neveneffecten en meldt een
      // no-op-tak niets. "Hele maand vastleggen" roept legWeekVast in een lus aan en levert
      // dus bewust één record per week.
      legWeekVast: (weekObject) => {
        let teMelden = null;
        set(state => {
          const isoJaar = weekObject.isoJaar ?? state.jaar;
          const weeknummer = weekObject.weeknummer;
          const sleutel = `${isoJaar}-${weeknummer}`;
          teMelden = { soort: 'vastleggen', isoJaar, weeknummer };
          return {
            vastgelegdeWeken: {
              ...state.vastgelegdeWeken,
              [sleutel]: {
                isoJaar,
                weeknummer,
                blok: weekObject.blok ?? null,
                // vandaagNL, niet toISOString: die schuift rond middernacht een dag terug,
                // en de server noteert wél de Nederlandse dag — zie lib/tijd.js.
                vastgelegdOp: vandaagNL(),
                week: JSON.parse(JSON.stringify(weekObject)), // diepe kopie
              },
            },
          };
        });
        if (teMelden) meldWijziging(teMelden);
      },

      // Maak de vastgelegde kopie van deze week weer gelijk aan de matrix. Zelfde
      // mechaniek als legWeekVast — een diepe kopie van het week-object overschrijft
      // de bestaande entry — maar met een eigen logboek-soort, want dit gebeurt alleen
      // na een geconstateerde divergentie (§36: 10 cellen, het team zag een ander
      // rooster dan de teamleider). vastgelegdOp blijft de OORSPRONKELIJKE datum: de
      // week is niet opnieuw vastgelegd, alleen hersteld.
      trekWeekGelijk: (weekObject, aantalAfwijkingen) => {
        let teMelden = null;
        set(state => {
          const isoJaar = weekObject.isoJaar ?? state.jaar;
          const weeknummer = weekObject.weeknummer;
          const sleutel = `${isoJaar}-${weeknummer}`;
          const bestaand = state.vastgelegdeWeken[sleutel];
          if (!bestaand) return {};   // niet vastgelegd → niets gelijk te trekken
          teMelden = {
            soort: 'gelijktrekken',
            isoJaar,
            weeknummer,
            detail: `${aantalAfwijkingen} afwijkende ${aantalAfwijkingen === 1 ? 'dienst' : 'diensten'} gelijkgetrokken aan de matrix`,
          };
          return {
            vastgelegdeWeken: {
              ...state.vastgelegdeWeken,
              [sleutel]: {
                ...bestaand,
                week: JSON.parse(JSON.stringify(weekObject)), // diepe kopie, zoals legWeekVast
              },
            },
          };
        });
        if (teMelden) meldWijziging(teMelden);
      },

      ontgrendelWeek: (isoJaar, weeknummer) => {
        let teMelden = null;
        set(state => {
          const sleutel = `${isoJaar}-${weeknummer}`;
          // Niet vastgelegd → niets te ontgrendelen. Zonder deze guard gaf de spread een
          // nieuw (inhoudelijk gelijk) object terug: een lege state-wijziging die wél een
          // server-sync plande en nu ook een onterecht logboek-record zou opleveren.
          if (!state.vastgelegdeWeken[sleutel]) return {};
          const { [sleutel]: _, ...rest } = state.vastgelegdeWeken;
          teMelden = { soort: 'ontgrendelen', isoJaar, weeknummer };
          return { vastgelegdeWeken: rest };
        });
        if (teMelden) meldWijziging(teMelden);
      },

      // ── Dienstruil-verzoeken (status-flow; NOG GEEN rooster-mutatie bij goedkeuring) ──
      // Statusflow: voorgesteld → tegendienst_gekozen → goedgekeurd | afgewezen.
      // Zijpaden: weigerRuilverzoek (geweigerd), trekRuilverzoekIn (ingetrokken).
      // Alle vijf muteren state.ruilverzoeken immutabel; een ongeldige overgang is een
      // no-op (guard op status). De echte dienst-wissel volgt in een latere stap.
      maakRuilverzoek: ({ indienerKey, indienerNaam, aangebodenDienst, collegaKey, collegaNaam }) =>
        set(state => {
          const verzoek = {
            id: `ruil_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            status: 'voorgesteld',
            aangemaaktOp: new Date().toISOString(),
            indienerKey,
            indienerNaam,
            aangebodenDienst,
            collegaKey,
            collegaNaam,
            tegendienst: null,
            gereageerdOp: null,
            beoordeeldOp: null,
          };
          return { ruilverzoeken: [...state.ruilverzoeken, verzoek] };
        }),

      // Collega kiest een tegendienst → status 'tegendienst_gekozen'. Alleen vanuit 'voorgesteld'.
      accepteerRuilverzoek: (id, tegendienst) =>
        set(state => ({
          ruilverzoeken: state.ruilverzoeken.map(r =>
            r.id === id && r.status === 'voorgesteld'
              ? { ...r, status: 'tegendienst_gekozen', tegendienst, gereageerdOp: new Date().toISOString() }
              : r
          ),
        })),

      // Collega weigert → status 'geweigerd'. Alleen vanuit 'voorgesteld'.
      weigerRuilverzoek: (id) =>
        set(state => ({
          ruilverzoeken: state.ruilverzoeken.map(r =>
            r.id === id && r.status === 'voorgesteld'
              ? { ...r, status: 'geweigerd', gereageerdOp: new Date().toISOString() }
              : r
          ),
        })),

      // Indiener trekt in → status 'ingetrokken'. Mag zolang nog niet beoordeeld.
      trekRuilverzoekIn: (id) =>
        set(state => ({
          ruilverzoeken: state.ruilverzoeken.map(r =>
            r.id === id && (r.status === 'voorgesteld' || r.status === 'tegendienst_gekozen')
              ? { ...r, status: 'ingetrokken' }
              : r
          ),
        })),

      // Teamleider beoordeelt → 'goedgekeurd' | 'afgewezen'. Alleen vanuit 'tegendienst_gekozen'.
      // NOG GEEN rooster-mutatie: de daadwerkelijke dienst-wissel bouwen we later.
      beoordeelRuilverzoek: (id, akkoord) =>
        set(state => ({
          ruilverzoeken: state.ruilverzoeken.map(r =>
            r.id === id && r.status === 'tegendienst_gekozen'
              ? { ...r, status: akkoord ? 'goedgekeurd' : 'afgewezen', beoordeeldOp: new Date().toISOString() }
              : r
          ),
        })),

      // ── Handmatig wijzigen: één cel in een (bewaard) maandrooster aanpassen ──
      // operatie: 'toevoegen' | 'weghalen'. Muteert rooster[gemKey][dienstId][dag]
      // immutabel in de live maandrooster, de bewaarde maandroosters[jaar-maand] EN
      // (indien vastgelegd) vastgelegdeWeken. Per bewerkte week draait analyseerWeek:
      // de analyse landt op de week (.analyse) en de engine-velden conflicten/
      // caoOvertredingen/urenTeller worden gesynct zodat de bestaande aggregatie-paden
      // (banner, selecteerMaand-restore, balans) live meebewegen. De blok-balans
      // (samenvattingPerBlok) wordt met het uren-delta bijgesteld. roosterStatus blijft
      // 'done', bewust GEEN ...resetRooster.
      wijzigCel: ({ weekIndex, gemKey, dienstId, dag, naam, operatie }) => {
        // Blijft null zolang niet vaststaat dat er ECHT iets verandert; beide no-op-takken
        // (geen liveWeek, en het ongewijzigde maandrooster verderop) melden daardoor niets.
        let teMelden = null;
        set(state => {
          const liveWeek = state.maandrooster?.weken?.[weekIndex];
          if (!liveWeek) return {};

          // Muteer rooster[gemKey][dienstId][dag] + (her)bereken de analyse voor deze week.
          const muteer = (week) => {
            const rooster = week.rooster || {};
            const huidig = rooster[gemKey]?.[dienstId]?.[dag] || [];
            const aanwezig = huidig.includes(naam);
            if (operatie === 'toevoegen' ? aanwezig : !aanwezig) return week; // no-op: niets te doen

            const nieuw = operatie === 'toevoegen' ? [...huidig, naam] : huidig.filter(n => n !== naam);
            const gem = rooster[gemKey] || {};
            const dienst = gem[dienstId] || {};
            const nieuweWeek = { ...week, rooster: { ...rooster, [gemKey]: { ...gem, [dienstId]: { ...dienst, [dag]: nieuw } } } };
            const vakantieDagen = new Set(vakantieDagenInWeek(nieuweWeek.weeknummer, nieuweWeek.isoJaar, state.vakanties));
            const analyse = analyseerWeek(
              nieuweWeek.rooster, state.regels, nieuweWeek.weeknummer, nieuweWeek.isoJaar,
              nieuweWeek.schoolweekType, { afwezigheid: {}, weekCaps: nieuweWeek.weekCaps, vakantieDagen },
            );
            nieuweWeek.analyse = analyse;
            nieuweWeek.conflicten = analyse.conflicten;            // engine-velden syncen zodat de
            nieuweWeek.caoOvertredingen = analyse.caoOvertredingen; // bestaande flatMap-paden
            nieuweWeek.urenTeller = analyse.urenTeller;             // (restore/aggregatie) kloppen
            return nieuweWeek;
          };

          // Vervang de week in een maandrooster + stel het bijbehorende blok-samenvatting-item
          // bij met het uren-delta (oud vs nieuw). Volledig immutabel.
          const pasToe = (mr) => {
            const oudeWeek = mr.weken[weekIndex];
            if (!oudeWeek) return mr;
            const nieuweWeek = muteer(oudeWeek);
            if (nieuweWeek === oudeWeek) return mr; // geen wijziging

            const oudUren = oudeWeek.analyse?.urenTeller ?? oudeWeek.urenTeller ?? {};
            const nieuwUren = nieuweWeek.analyse.urenTeller;
            const delta = {};
            for (const k of new Set([...Object.keys(oudUren), ...Object.keys(nieuwUren)])) {
              const d = (nieuwUren[k] || 0) - (oudUren[k] || 0);
              if (d !== 0) delta[k] = d;
            }

            const weken = mr.weken.map((w, i) => (i === weekIndex ? nieuweWeek : w));
            let samenvattingPerBlok = mr.samenvattingPerBlok;
            if (samenvattingPerBlok && Object.keys(delta).length) {
              samenvattingPerBlok = samenvattingPerBlok.map(b => {
                if (b.blok !== oudeWeek.blok || b.isoJaar !== oudeWeek.isoJaar) return b;
                const samenvatting = { ...b.samenvatting };
                for (const [k, d] of Object.entries(delta)) {
                  if (samenvatting[k]) samenvatting[k] = { ...samenvatting[k], cumUren: samenvatting[k].cumUren + d, afwijking: samenvatting[k].afwijking + d };
                }
                return { ...b, samenvatting };
              });
            }
            return { ...mr, weken, samenvattingPerBlok };
          };

          const nieuwMaandrooster = pasToe(state.maandrooster);
          if (nieuwMaandrooster === state.maandrooster) return {}; // no-op

          // Vanaf hier staat vast dat de cel echt verandert → melden aan het logboek (na set()).
          teMelden = {
            soort: 'cel',
            isoJaar: liveWeek.isoJaar ?? null,
            weeknummer: liveWeek.weeknummer ?? null,
            gemeenteKey: gemKey,
            dienstId,
            dag,
            wieNaam: naam,
            operatie,
          };

          // Her-aggregeer de platte banner-velden uit alle weken. De conflicten komen vers uit
          // het rooster (zie verseConflicten): ook een zojuist bewerkte week kan een .analyse
          // uit een oudere sessie dragen — partialize schrijft maandroosters/vastgelegdeWeken
          // in hun geheel weg, dus .analyse overleeft een reload.
          const conflicten = nieuwMaandrooster.weken.flatMap(w => verseConflicten(w, state));
          const caoOvertredingen = nieuwMaandrooster.weken.flatMap(w => w.analyse?.caoOvertredingen ?? w.caoOvertredingen ?? []);

          const updates = { maandrooster: nieuwMaandrooster, conflicten, caoOvertredingen, roosterStatus: 'done' };

          // bewaarde kopie van de actieve maand (sleutel jaar-maand uit maandSelectie)
          const sel = state.maandSelectie;
          const maandKey = sel ? `${sel.jaar}-${sel.maand}` : null;
          if (maandKey && state.maandroosters[maandKey]?.weken?.[weekIndex]) {
            updates.maandroosters = { ...state.maandroosters, [maandKey]: pasToe(state.maandroosters[maandKey]) };
          }

          // vastgelegde kopie van deze week (sleutel isoJaar-weeknummer)
          const vastKey = `${liveWeek.isoJaar}-${liveWeek.weeknummer}`;
          const vastEntry = state.vastgelegdeWeken[vastKey];
          if (vastEntry?.week) {
            updates.vastgelegdeWeken = { ...state.vastgelegdeWeken, [vastKey]: { ...vastEntry, week: muteer(vastEntry.week) } };
          }

          return updates;
        });
        if (teMelden) meldWijziging(teMelden);
      },

      // ── Medewerkers CRUD ─────────────────────────────────────────────────

      updateMedewerker: (key, updates) =>
        set(state => ({
          regels: {
            ...state.regels,
            medewerkers: { ...state.regels.medewerkers, [key]: { ...state.regels.medewerkers[key], ...updates } },
          },
          ...resetRooster,
        })),

      addMedewerker: (data) => {
        const key = maakMedewerkerSleutel(data.naam);
        set(state => ({
          regels: { ...state.regels, medewerkers: { ...state.regels.medewerkers, [key]: data } },
          ...resetRooster,
        }));
        return key;
      },

      removeMedewerker: (key) =>
        set(state => {
          const { [key]: _, ...rest } = state.regels.medewerkers;
          return { regels: { ...state.regels, medewerkers: rest }, ...resetRooster };
        }),

      // ── Gemeenten CRUD ───────────────────────────────────────────────────

      updateGemeente: (gemKey, updates) =>
        set(state => ({
          regels: updateGemeenteIn(state.regels, gemKey, updates),
          ...resetRooster,
        })),

      // data = { naam, diensten: {}, actief: true }. Via updateGemeenteIn, dus een
      // BESTAANDE key zou hier gemuteerd worden i.p.v. aangemaakt — de aanroeper
      // controleert op een dubbele sleutel (Beheer.jsx, GemeentenTab).
      addGemeente: (gemKey, data) =>
        set(state => ({
          regels: updateGemeenteIn(state.regels, gemKey, data),
          ...resetRooster,
        })),

      // Spiegelt removeMedewerker/removeDienstIn: alleen de key uit regels.gemeenten
      // halen, verder NIETS opruimen. Verwijzingen elders (mw.gemeenten[],
      // voorkeur_gemeente, vast_weekend_gemeente, oude maandroosters en
      // vastgelegdeWeken) blijven bewust staan — exact wat er bij een verwijderde
      // medewerker (Robby) ook gebeurde. Dat is veilig: de engine loopt over
      // regels.gemeenten en slaat een verweesde gemKey in het rooster over
      // (analyseer-week.js), elke regels.gemeenten[...]-lookup op medewerker-data is
      // direct afgevangen (rooster-engine.js, blok-generator.js, verlof-check.js,
      // fte.js) en server-side valt de displaynaam terug op de kale key
      // (gemeenteNaam/dienstLabel in rooster-service.js). Een vastgelegd rooster blijft
      // dus leesbaar.
      removeGemeente: (gemKey) =>
        set(state => {
          const { [gemKey]: _, ...rest } = state.regels.gemeenten;
          return { regels: { ...state.regels, gemeenten: rest }, ...resetRooster };
        }),

      // ── Diensten CRUD ────────────────────────────────────────────────────

      updateDienst: (gemKey, dienstKey, updates) =>
        set(state => ({
          regels: updateDienstIn(state.regels, gemKey, dienstKey, updates),
          ...resetRooster,
        })),

      addDienst: (gemKey, dienstKey, data) =>
        set(state => {
          const gem = state.regels.gemeenten[gemKey];
          return {
            regels: updateGemeenteIn(state.regels, gemKey, {
              diensten: { ...gem.diensten, [dienstKey]: data },
            }),
            ...resetRooster,
          };
        }),

      removeDienst: (gemKey, dienstKey) =>
        set(state => ({
          regels: removeDienstIn(state.regels, gemKey, dienstKey),
          ...resetRooster,
        })),

      // ── Reset ────────────────────────────────────────────────────────────

      resetRegels: () =>
        set({ regels: defaultRegels, afwezigheid: [], vakanties: SCHOOLVAKANTIES_ZUID, ...resetRooster }),

      // ── Back-up (import) ──────────────────────────────────────────────────
      // Vervangt de volledige persisted state door een geimporteerde back-up.
      // Ontbrekende velden vallen terug op de huidige waarde (undefined = mist;
      // null is een geldige waarde en blijft behouden). resetRooster wist de
      // runtime engine-output zodat scherm en data consistent blijven; de
      // persist-middleware schrijft de nieuwe state meteen naar localStorage.
      importBackup: (data) => {
        const huidig = get();
        const kies = (key) => (data && data[key] !== undefined ? data[key] : huidig[key]);
        set({
          weeknummer: kies('weeknummer'),
          jaar: kies('jaar'),
          afwezigheid: kies('afwezigheid'),
          vakanties: kies('vakanties'),
          bijzonderheden: kies('bijzonderheden'),
          celOverrides: kies('celOverrides'),
          afgevinkteMeldingen: kies('afgevinkteMeldingen'),
          ruilverzoeken: kies('ruilverzoeken'),
          geselecteerdeMedewerker: kies('geselecteerdeMedewerker'),
          regels: kies('regels'),
          vastgelegdeWeken: kies('vastgelegdeWeken'),
          maandroosters: kies('maandroosters'),
          ...resetRooster,
        });
      },

      // ── Mijn Rooster ─────────────────────────────────────────────────────

      geselecteerdeMedewerker: null,
      setGeselecteerdeMedewerker: (naam) => set({ geselecteerdeMedewerker: naam }),

      // ── Maand-selectie (Regel B: de maandag bepaalt de maand) ────────────

      // Selecteert (jaar, maand) zonder te genereren: weeknummer/jaar springen
      // naar de eerste week van de maand (incl. het juiste isoJaar van die week).
      selecteerMaand: (jaar, maand) => {
        const { weken } = maandWeken(jaar, maand);
        const basis = { weeknummer: weken[0].week, jaar: weken[0].isoJaar, maandSelectie: { jaar, maand }, bekekenMaand: { jaar, maand } };
        const bewaard = get().maandroosters[`${jaar}-${maand}`];
        if (bewaard) {
          // Herstel de laatst bewaarde generatie van deze maand (diepe kopie) en leid de
          // afgeleide velden opnieuw af uit de weken (zelfde flatMap-logica als genereerMaand).
          const maandrooster = JSON.parse(JSON.stringify(bewaard));
          const { regels, afwezigheid, vakanties } = get();
          set({
            ...basis,
            maandrooster,
            roosterLog: maandrooster.weken.flatMap(w =>
              [`=== Week ${w.weeknummer} (blok ${w.blok}) ===`, ...w.log]
            ),
            conflicten: maandrooster.weken.flatMap(w => verseConflicten(w, { regels, afwezigheid, vakanties })),
            caoOvertredingen: maandrooster.weken.flatMap(w => w.caoOvertredingen || []),
            roosterStatus: 'done',
          });
        } else {
          set({ ...basis, ...resetRooster });
        }
      },

      // Genereert de maand via complete 4-weken-blokken (anker op ISO-week 1):
      // de blok-generator rekent de dekkende blokken door en de maand-weken
      // worden daaruit geknipt (maand = kijkvenster, blok = rekeneenheid).
      // maandrooster krijgt hier de blok-vorm: weken[] met bloknummer per week
      // + samenvattingPerBlok i.p.v. één venster-samenvatting.
      genereerMaand: (jaar, maand) => {
        get().selecteerMaand(jaar, maand);
        const { afwezigheid, regels, vastgelegdeWeken, vakanties } = get();
        set({ roosterStatus: 'loading' });
        try {
          // Globale vastgelegde-set voor de engine: sleutel `${isoJaar}:${weeknummer}`
          // (DUBBELE PUNT — de blok-generator filtert hierop; store gebruikt een STREEPJE).
          // De waarde is het volledige engine-weekobject (.week).
          const vastgelegdeSet = {};
          for (const v of Object.values(vastgelegdeWeken || {})) {
            vastgelegdeSet[`${v.isoJaar}:${v.weeknummer}`] = v.week;
          }
          const resultaat = genereerMaandViaBlokken(regels, afwezigheid, jaar, maand, vastgelegdeSet, vakanties);
          const nieuwMaandrooster = {
            weken: resultaat.weergave,
            samenvattingPerBlok: resultaat.blokken.map(b => ({
              blok: b.blok, isoJaar: b.isoJaar, weken: b.weken,
              inMaand: b.inMaand, bevatWeek53: b.bevatWeek53,
              samenvatting: b.samenvatting,
            })),
            // De afwezigheid waarmee deze generatie is berekend.
            // ⚠️ WORDT GESCHREVEN, MAAR NERGENS MEER GELEZEN. Er hing één lezer aan: de melding
            // "verlof/ziekte gewijzigd sinds deze generatie" in Weekrooster.jsx. Die is er bewust
            // uit — hij dubbelde met de V/Z in de matrix en met de onderbezettingsmelding in
            // Knelpunten. Het veld blijft staan omdat het in elk bestaand opgeslagen maandrooster
            // zit (localStorage én server); het weghalen zou een datawijziging zijn zonder winst,
            // en het is de enige vastlegging van waarmee er destijds gegenereerd is.
            // Zoek je de lezer: die is er niet meer, en dat is geen omissie.
            gegenereerdMetAfwezigheid: JSON.stringify(afwezigheid),
          };
          set(state => ({
            maandrooster: nieuwMaandrooster,
            // Bewaar de laatste generatie van deze maand (diepe kopie, los van de live state).
            maandroosters: {
              ...state.maandroosters,
              [`${jaar}-${maand}`]: JSON.parse(JSON.stringify(nieuwMaandrooster)),
            },
            roosterLog: resultaat.weergave.flatMap(w =>
              [`=== Week ${w.weeknummer} (blok ${w.blok}) ===`, ...w.log]
            ),
            // Vers, niet w.conflicten: een vastgelegde week komt ongewijzigd uit de engine
            // terug en draagt de conflicten van de generatie van toen (zie verseConflicten).
            conflicten: resultaat.weergave.flatMap(w => verseConflicten(w, { regels, afwezigheid, vakanties })),
            caoOvertredingen: resultaat.weergave.flatMap(w => w.caoOvertredingen || []),
            roosterStatus: 'done',
          }));
        } catch (err) {
          logError(err, { source: 'engine', jaar, maand });
          set({ roosterStatus: 'error', roosterLog: [err.message] });
        }
      },

      // ── Rooster genereren (aantalWeken, default 4) ───────────────────────

      // wekenInfo (optioneel): [{ week, isoJaar }] per venster-positie. Zonder wekenInfo rekent
      // weekOpPositie de echte (week, isoJaar) per positie uit via de kalender. Alles hieronder
      // staat op die ECHTE week: de maps, de vastgelegde weken én het nummer waarmee de engine
      // rekent, ook in een venster dat over het einde van een ISO-jaar loopt.
      genereerRooster: (aantalWeken = 4, wekenInfo = null) => {
        const { weeknummer, jaar, afwezigheid, regels, vastgelegdeWeken, vakanties } = get();
        set({ roosterStatus: 'loading' });
        try {
          const afwMaps = {};
          const afwTypeMaps = {};
          const afwUrenMaps = {};
          const vakantieMaps = {};
          // Vastgelegde weken voor dit venster, op de echte week gesleuteld (zo zoekt de engine
          // ze op). Per positie de store-sleutel `${isoJaar}-${week}` opzoeken; waarde = het
          // volledige engine-weekobject.
          const vastgelegdeSet = {};
          // De ECHTE (week, isoJaar) per positie. De engine krijgt de weeknummers als lijst; het
          // isoJaar zet de store er na afloop bij, zie hieronder.
          const infos = [];
          for (let i = 0; i < aantalWeken; i++) {
            const info = wekenInfo?.[i] || weekOpPositie(weeknummer, jaar, i);
            infos.push(info);
            afwMaps[info.week] = bouwAfwezigheidMap(afwezigheid, info.week, info.isoJaar);
            afwTypeMaps[info.week] = bouwAfwezigheidTypeMap(afwezigheid, info.week, info.isoJaar);
            // Handmatig ingevulde uren (verlof, ziekte), per dag al verdeeld over de hele periode.
            // Op de echte week gesleuteld, net als de twee maps hierboven; die week gaat ook als
            // argument mee, want daar hangt de dag-toewijzing van af.
            afwUrenMaps[info.week] = bouwAfwezigheidUrenMap(afwezigheid, info.week, info.isoJaar, regels, vakanties);
            // Per-week vakantiedagen (Set) met het juiste isoJaar; expliciete state.vakanties.
            vakantieMaps[info.week] = new Set(vakantieDagenInWeek(info.week, info.isoJaar, vakanties));
            const vast = vastgelegdeWeken[`${info.isoJaar}-${info.week}`];
            if (vast) vastgelegdeSet[info.week] = vast.week;
          }
          // De engine krijgt de LIJST echte weeknummers, niet het startnummer. Met een getal telt
          // hij startWeek + i door, en vanaf week 51 liep dat over het jaareinde heen naar 54, 55,
          // 56. Na een jaar met 53 weken klapten daarmee het A/B-type en de weekendrotatie om, en
          // een vastgelegde week voorbij het jaareinde werd niet meer herkend, waarna de engine in
          // het opgeslagen weekobject schreef (test/diag/jaargrens-meting.mjs). Binnen één ISO-jaar
          // is de lijst precies startWeek + i, dus daar verandert er niets.
          const resultaat = genereerMaandroosterEngine(regels, afwMaps, infos.map((info) => info.week), aantalWeken, vastgelegdeSet, afwTypeMaps, vakantieMaps, null, afwUrenMaps);

          // isoJaar erbij zetten: de engine kent dat veld niet, waardoor de 4-wekenmodus het
          // nergens had. verseConflicten viel daardoor terug op het huidige KALENDERJAAR
          // (bouwAfwezigheidMap-default) en zocht afwezigheid op met een gegokt jaar.
          // weeknummer gaat ook mee. De engine rekende al met de echte week, dus dat verandert
          // niets, maar zo staat de week vast, ook voor een vastgelegde week die ongewijzigd uit
          // de engine terugkomt. De koppeling loopt op INDEX (weken[i] ↔ infos[i]), niet op nummer.
          const weken = resultaat.weken.map((w, i) => ({
            ...w,
            weeknummer: infos[i].week,
            isoJaar: infos[i].isoJaar,
          }));

          set({
            // Mee-bakken zoals genereerMaand dat doet — ook hier geschreven maar niet gelezen;
            // zie de toelichting daar.
            maandrooster: { ...resultaat, weken, gegenereerdMetAfwezigheid: JSON.stringify(afwezigheid) },
            // w.weeknummer, niet weeknummer + i: dat is de echte week, en zo staat het log
            // gelijk aan de maandmodus (zie genereerMaand).
            roosterLog: weken.flatMap(w => [`=== Week ${w.weeknummer} ===`, ...w.log]),
            // Vers, niet w.conflicten: dit venster kan vastgelegde weken bevatten (vastgelegdeSet
            // hierboven) en die komen ongewijzigd uit de engine terug — zie verseConflicten.
            conflicten: weken.flatMap(w => verseConflicten(w, { regels, afwezigheid, vakanties })),
            caoOvertredingen: weken.flatMap(w => w.caoOvertredingen || []),
            roosterStatus: 'done',
          });
        } catch (err) {
          logError(err, { source: 'engine', weeknummer, jaar });
          set({ roosterStatus: 'error', roosterLog: [err.message] });
        }
      },
    }),
    {
      name: 'sass-rooster-v4',
      version: 3,
      // Niet de kale naam als localStorage-sleutel, maar een bucket per omgeving — zie
      // omgevingStorage hierboven. De persist-vorm (partialize/migrate/version) blijft gelijk.
      storage: createJSONStorage(() => omgevingStorage),
      // Geen veld-resync vanuit regels.json: localStorage (Beheer) is de bron van
      // waarheid en mag nooit door de seed worden overschreven. migrate geeft de
      // opgeslagen state terug zodat een client met een oudere versie zijn eigen
      // localStorage houdt (zonder migrate valt Zustand terug op de seed-defaults).
      // v2→v3: de weeknummer-definitie ging van niet-ISO naar ISO-8601. Het oude
      // opgeslagen weeknummer (mogelijk 1 week verschoven) vervalt daarom eenmalig
      // zodat de init vers ISO rekent; regels/afwezigheid/bijzonderheden/jaar
      // blijven onaangetast.
      migrate: (persistedState) => {
        const { weeknummer: _weeknummer, ...rest } = persistedState || {};
        return rest;
      },
      partialize: (state) => ({
        weeknummer: state.weeknummer,
        jaar: state.jaar,
        afwezigheid: state.afwezigheid,
        vakanties: state.vakanties,
        bijzonderheden: state.bijzonderheden,
        celOverrides: state.celOverrides,
        afgevinkteMeldingen: state.afgevinkteMeldingen,
        ruilverzoeken: state.ruilverzoeken,
        geselecteerdeMedewerker: state.geselecteerdeMedewerker,
        regels: state.regels,
        vastgelegdeWeken: state.vastgelegdeWeken,
        maandroosters: state.maandroosters,
      }),
    }
  )
);
